import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { mapToParentPlatform } from '../utils/platformMapping';
import { MessageBubble } from './ai/MessageBubble';
import { WelcomeScreen } from './ai/WelcomeScreen';

type ProductSource = {
  id: number;
  platform: string;
  solution: string;
};

type CitationData = {
  platform: string;
  solution: string;
  tab: string;
};

type Message = {
  role: 'user' | 'assistant';
  content: string;
  sources?: ProductSource[];
  citationMetadata?: Record<string, CitationData>;
};

type AIAssistantProps = {
  isOpen: boolean;
  onClose: () => void;
};

function AIAssistant({ isOpen, onClose }: AIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { aiDrawerWidth, setAIDrawerWidth } = useProductMatrix();

  const MIN_WIDTH = 300;
  const MAX_WIDTH = 800;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Lock body scroll on mobile when drawer is open
  useEffect(() => {
    if (isOpen) {
      // Only lock on mobile (max-lg)
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from AI assistant');
      }

      // Read the text stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulatedText = '';
      let citationMetadata: Record<string, CitationData> = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // Parse the data stream format:
        // "0:" = text chunks
        // "1:" = citation metadata
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('0:')) {
            // Extract text after "0:" and properly parse JSON string to handle escape sequences
            try {
              const jsonString = line.substring(2);
              const text = JSON.parse(jsonString);
              accumulatedText += text;
            } catch (e) {
              // Fallback to simple string replacement if JSON parsing fails
              const text = line.substring(2).replace(/^"/, '').replace(/"$/, '');
              accumulatedText += text;
            }
          } else if (line.startsWith('1:')) {
            // Citation metadata
            try {
              const jsonString = line.substring(2);
              citationMetadata = JSON.parse(jsonString);
              console.log('Received citation metadata:', citationMetadata);
            } catch (e) {
              console.error('Failed to parse citation metadata:', e);
            }
          }
        }

        // Update the assistant message in real-time
        // Capture current values to avoid no-loop-func lint warning
        const currentText = accumulatedText;
        const currentCitation = citationMetadata;
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];

          if (lastMessage?.role === 'assistant') {
            lastMessage.content = currentText;
            lastMessage.citationMetadata = currentCitation;
          } else {
            newMessages.push({
              role: 'assistant',
              content: currentText,
              citationMetadata: currentCitation,
            });
          }

          return newMessages;
        });
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCitationClick = (citationNumber: string, citationMetadata: Record<string, CitationData>) => {
    const number = citationNumber.replace(/[[\]]/g, '');
    const citation = citationMetadata[number];

    if (citation) {
      const { platform, tab } = mapToParentPlatform(citation.platform);
      const platformSlug = encodeURIComponent(platform);
      const solutionSlug = encodeURIComponent(citation.solution);
      const finalTab = tab !== 'product-modules' ? tab : citation.tab;
      navigate(
        `/platform/${platformSlug}?tab=${finalTab}&highlight=${solutionSlug}&from=ai-assistant`
      );
    }
  };

  const clearConversation = () => {
    setMessages([]);
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (isLoading) return;
    const userMessage: Message = { role: 'user', content: suggestion };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [userMessage].map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (!response.ok) throw new Error('Failed to get response from AI assistant');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulatedText = '';
      let citationMetadata: Record<string, CitationData> = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (line.startsWith('0:')) {
            try {
              accumulatedText += JSON.parse(line.substring(2));
            } catch {
              accumulatedText += line.substring(2).replace(/^"/, '').replace(/"$/, '');
            }
          } else if (line.startsWith('1:')) {
            try {
              citationMetadata = JSON.parse(line.substring(2));
            } catch {
              console.error('Failed to parse citation metadata');
            }
          }
        }

        const currentText = accumulatedText;
        const currentCitation = citationMetadata;
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === 'assistant') {
            lastMessage.content = currentText;
            lastMessage.citationMetadata = currentCitation;
          } else {
            newMessages.push({ role: 'assistant', content: currentText, citationMetadata: currentCitation });
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Drag handling for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate new width based on mouse position from right edge
      const newWidth = window.innerWidth - e.clientX;

      // Constrain within min and max bounds
      const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

      setAIDrawerWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, setAIDrawerWidth]);

  return (
    <>
      {/* Right-side Drawer */}
      <aside
        className={`
          fixed top-0 right-0 h-screen
          bg-white dark:bg-gray-900 border-l dark:border-gray-700
          flex flex-col
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          z-40
        `}
        style={{
          width: window.innerWidth >= 1024 ? `${aiDrawerWidth}px` : '100%',
        }}
      >
        {/* Resize Handle - Desktop Only */}
        <div
          className="hidden lg:block absolute inset-y-0 left-0 w-2 cursor-col-resize hover:bg-blue-500/10 active:bg-blue-500/20"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute inset-y-0 left-0 w-px bg-gray-200" />
          <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-1">
            <div className="h-1 w-1 rounded-full bg-gray-400" />
            <div className="h-1 w-1 rounded-full bg-gray-400" />
            <div className="h-1 w-1 rounded-full bg-gray-400" />
          </div>
        </div>
        {/* Header - Sticky */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-900 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">AI Product Assistant</h3>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              aria-label="Close AI Assistant"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
          ) : (
            <>
              {messages.map((message, index) => (
                <MessageBubble
                  key={index}
                  message={message}
                  onCitationClick={handleCitationClick}
                />
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="text-gray-900 dark:text-gray-100 text-sm">
                    <span
                      className="inline-block w-0.5 h-4 bg-gray-900 dark:bg-gray-100"
                      style={{ animation: 'blink 0.8s infinite' }}
                    ></span>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - Sticky Bottom */}
        <form
          onSubmit={handleSubmit}
          className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-900"
        >
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about products, availability, etc..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
              disabled={isLoading}
              autoFocus={isOpen}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

export default AIAssistant;
